/**
 * Documents API client — Story 94-9 frontend glue for the @kgc/documents backend.
 *
 * Wraps the 5 DocumentsController endpoints exposed by the kgc-api:
 *   POST   /documents/upload          — multipart upload (BOLTVEZETO+)
 *   GET    /documents                 — list by (sourceType, sourceId) (BOLTVEZETO+)
 *   GET    /documents/:id/download    — binary download (BOLTVEZETO+)
 *   DELETE /documents/:id             — soft delete (BOLTVEZETO+)
 *   GET    /documents/audit           — admin audit log (ADMIN+)
 *
 * Response shapes mirror the controller return types. `fileSize` is a
 * stringified bigint (JSON-safe). Timestamps are ISO strings.
 *
 * @see apps/kgc-api/src/modules/documents/documents.controller.ts
 * @see planning-artifacts/adr/ADR-092-kgc-documents-storage-service.md
 */

import { api } from './client';

// ═════════════════════════════════════════════════════════════════════════
// TYPES — kept in sync with DocumentsController response DTOs
// ═════════════════════════════════════════════════════════════════════════

/**
 * Matches the Prisma `DocumentSourceType` enum in
 * `apps/kgc-api/prisma/schema/enums.prisma`. 10 values covering
 * every business entity that can own a document (delivery note, worksheet,
 * vehicle, partner, invoice, quote, rental contract, service warranty,
 * chat attachment, goods receipt).
 */
export type DocumentSourceType =
  | 'DELIVERY_NOTE'
  | 'WORKSHEET'
  | 'VEHICLE'
  | 'PARTNER'
  | 'INVOICE'
  | 'QUOTE'
  | 'RENTAL_CONTRACT'
  | 'SERVICE_WARRANTY'
  | 'CHAT_ATTACHMENT'
  | 'GOODS_RECEIPT';

/**
 * Matches the Prisma `DocumentLegalBasis` enum in
 * `apps/kgc-api/prisma/schema/enums.prisma`. GDPR Art. 6 jogalap
 * szerint — drives retention calculation in `DocumentStorageService.upload()`.
 *
 * ACCOUNTING_RECORD → Szt. 169.§ 8 év, NEM törölhető erasure request-re.
 * CONSENT → visszavonható, törölhető.
 * CONTRACT → szerződésteljesítés.
 * LEGITIMATE_INTEREST → eseti elbírálás.
 * LEGAL_OBLIGATION → egyéb jogszabály (nem számvitel).
 */
export type DocumentLegalBasis =
  | 'ACCOUNTING_RECORD'
  | 'CONSENT'
  | 'CONTRACT'
  | 'LEGITIMATE_INTEREST'
  | 'LEGAL_OBLIGATION';

/**
 * Matches the Prisma `DocumentAuditAction` enum in
 * `apps/kgc-api/prisma/schema/enums.prisma`. 10 értékek a
 * dokumentum életciklus eseményeihez (UPLOADED..ORPHAN_DETECTED). Drives the
 * admin audit page filter dropdown.
 */
export type DocumentAuditAction =
  | 'UPLOADED'
  | 'DOWNLOADED'
  | 'VIEWED'
  | 'SHARED_VIA_LINK'
  | 'UPDATED'
  | 'DELETED'
  | 'RESTORED'
  | 'HASH_VERIFIED'
  | 'HASH_MISMATCH'
  | 'ORPHAN_DETECTED';

/** Serialized Document as returned by `GET /documents`. */
export interface DocumentListItem {
  id: string;
  fileName: string;
  mimeType: string;
  /** bigint → string to survive JSON without precision loss. */
  fileSize: string;
  contentHash: string;
  uploadedBy: string;
  uploadedAt: string;
  legalBasis: string;
  accessRoles: string[];
}

/** Slim response from `POST /documents/upload`. */
export interface UploadedDocument {
  id: string;
  fileName: string;
  fileSize: string;
  contentHash: string;
}

/** One row in `GET /documents/audit`. */
export interface DocumentAuditItem {
  id: string;
  documentId: string;
  action: string;
  performedBy: string;
  performedAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: unknown;
}

export interface DocumentAuditResponse {
  data: DocumentAuditItem[];
  meta: { total: number; skip: number; take: number };
}

export interface DocumentAuditQuery {
  documentId?: string;
  action?: DocumentAuditAction;
  skip?: number;
  take?: number;
}

export interface UploadDocumentInput {
  file: File;
  sourceType: DocumentSourceType;
  sourceId: string;
  legalBasis: DocumentLegalBasis;
  /** Optional role whitelist — empty array means "visible to all roles". */
  accessRoles?: string[];
  /** Optional retention override in days. Defaults server-side. */
  retentionDays?: number;
}

// ═════════════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═════════════════════════════════════════════════════════════════════════

/**
 * List documents for a given source (e.g. all attachments on a delivery
 * note). Returns an empty array when nothing is attached.
 */
export async function listDocuments(
  sourceType: DocumentSourceType,
  sourceId: string
): Promise<{ data: DocumentListItem[] }> {
  const params = new URLSearchParams({ sourceType, sourceId });
  return api.get<{ data: DocumentListItem[] }>(`/documents?${params.toString()}`);
}

/**
 * Upload a new document. The browser sets the multipart boundary header
 * automatically — do NOT add `Content-Type` manually.
 */
export async function uploadDocument(
  input: UploadDocumentInput
): Promise<{ data: UploadedDocument }> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('sourceType', input.sourceType);
  formData.append('sourceId', input.sourceId);
  formData.append('legalBasis', input.legalBasis);
  if (input.accessRoles && input.accessRoles.length > 0) {
    formData.append('accessRoles', input.accessRoles.join(','));
  }
  if (input.retentionDays !== undefined) {
    formData.append('retentionDays', String(input.retentionDays));
  }
  return api.upload<{ data: UploadedDocument }>('/documents/upload', formData);
}

/**
 * Error thrown when the backend-provided `X-Content-SHA256` header does not
 * match the SHA-256 hash of the downloaded bytes. Indicates in-flight
 * corruption (nginx buffering, proxy tampering) or a stale storage object
 * that the CRON HashIntegrityService will detect during the next sweep.
 * Adversarial CRITICAL fix.
 */
export class DocumentHashMismatchError extends Error {
  constructor(
    public readonly expectedHash: string,
    public readonly actualHash: string
  ) {
    super(
      `Content hash mismatch: expected ${expectedHash.slice(0, 16)}…, got ${actualHash.slice(0, 16)}…`
    );
    this.name = 'DocumentHashMismatchError';
  }
}

/**
 * Convert an ArrayBuffer of SHA-256 bytes to a lowercase hex string.
 * The backend `DocumentStorageService` produces hex via `crypto.createHash`
 * so the client must match the same encoding for comparison.
 */
function bufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let hex = '';
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Download a document as a browser blob, verify its SHA-256 hash against
 * the `X-Content-SHA256` response header, then trigger the native "save as"
 * dialog. Uses the passed `fileName` for the download attribute — Chrome
 * and Firefox also respect the server's `Content-Disposition: filename*`
 * but we pass an explicit client-known fallback for non-UTF-8 safety.
 *
 * SECURITY: hash verification is mandatory. If the backend emits a
 * `X-Content-SHA256` header and the recomputed hash doesn't match, the
 * function throws `DocumentHashMismatchError` and the blob URL is revoked
 * without triggering the download. This defends against:
 *   - nginx buffer truncation on abrupt TCP resets
 *   - proxy middlebox tampering
 *   - stale MinIO object drift (Story 94-7 CRON will eventually catch it,
 *     but the client-side check is immediate)
 *
 * If the backend does NOT emit the header (e.g. older API version), we
 * log a warning and proceed with the download — backwards-compatible.
 * Adversarial CRITICAL fix.
 */
export async function downloadDocument(id: string, fileName: string): Promise<void> {
  const { blob, headers } = await api.downloadBlobWithHeaders(`/documents/${id}/download`);

  // Verify the hash BEFORE handing the blob to the user.
  const expectedHash = headers.get('x-content-sha256');
  if (expectedHash !== null && expectedHash !== '') {
    const actualBuffer = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    const actualHash = bufferToHex(actualBuffer);
    if (actualHash !== expectedHash.toLowerCase()) {
      throw new DocumentHashMismatchError(expectedHash, actualHash);
    }
  } else {
    console.warn(
      `[downloadDocument] ${id}: backend did not provide X-Content-SHA256 header — skipping integrity check`
    );
  }

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Release the blob URL even if link.click() throws (popup blocker, etc.).
    // Adversarial MEDIUM fix.
    URL.revokeObjectURL(url);
  }
}

/**
 * Soft delete (marks `isDeleted = true`, keeps the row + storage file for
 * audit and Szt. 169.§ 8-year retention). Backend returns
 * `{ data: { success: true } }` (200 OK).
 */
export async function deleteDocument(id: string): Promise<void> {
  await api.delete<{ data: { success: true } }>(`/documents/${id}`);
}

/**
 * Admin-only audit log query. Backend enforces ADMIN role; calling this
 * without the role returns 403 at the guard.
 */
export async function listDocumentAudit(
  query: DocumentAuditQuery = {}
): Promise<DocumentAuditResponse> {
  const params = new URLSearchParams();
  if (query.documentId !== undefined) params.set('documentId', query.documentId);
  if (query.action !== undefined) params.set('action', query.action);
  if (query.skip !== undefined) params.set('skip', String(query.skip));
  if (query.take !== undefined) params.set('take', String(query.take));
  const qs = params.toString();
  return api.get<DocumentAuditResponse>(`/documents/audit${qs ? `?${qs}` : ''}`);
}
